import { useState, useEffect, useRef } from "react";
import { ArrowRight, Mail, MapPin, Shield, ChevronDown } from "lucide-react";
import Reveal from "../components/Reveal";

declare global {
  interface Window {
    onTurnstileReady?: () => void;
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; theme: string }) => string;
      remove: (id: string) => void;
      getResponse: (id?: string) => string | undefined;
    };
  }
}

interface FormState { name: string; email: string; company: string; interest: string; message: string; }
const EMPTY: FormState = { name: "", email: "", company: "", interest: "AI Integration", message: "" };

const OPTIONS = [
  "AI Integration",
  "Custom AI Product",
  "AI Transformation Consultancy",
  "Defence-Ready Audit",
  "Policy Generation",
  "Rapid Prototype / AI Rescue",
  "Managed Support",
  "Other",
] as const;

export default function Contact() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState("");
  const widgetRef = useRef<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close custom dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Explicit Turnstile render — avoids the async-script + React SPA race condition.
  // Runs whenever the form mounts/unmounts (sent state toggle).
  useEffect(() => {
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    if (!siteKey || sent) return;

    let active = true;

    const doRender = () => {
      if (!active) return;
      const el = document.getElementById("turnstile-widget");
      if (!el || widgetRef.current) return;
      widgetRef.current = window.turnstile?.render(el, { sitekey: siteKey, theme: "dark" }) ?? null;
    };

    if (window.turnstile) {
      doRender();
    } else {
      window.onTurnstileReady = doRender;
    }

    return () => {
      active = false;
      if (widgetRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetRef.current); } catch { /* ignore */ }
        widgetRef.current = null;
      }
    };
  }, [sent]);

  const upd = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const er: typeof errors = {};
    if (!form.name.trim()) er.name = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Valid email required";
    if (!form.message.trim() || form.message.trim().length < 12) er.message = "Tell us a bit more (12+ chars)";
    setErrors(er);
    if (Object.keys(er).length) return;

    const turnstileEnabled = import.meta.env.VITE_TURNSTILE_ENABLED === "true";
    const turnstileToken =
      document.querySelector<HTMLInputElement>('[name="cf-turnstile-response"]')?.value ?? "";
    if (turnstileEnabled && !turnstileToken) {
      setFormError("Please wait for the security check to complete");
      return;
    }

    setSending(true);
    setFormError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, turnstileToken }),
      });
      if (!res.ok) {
        const data = await res.json() as { fields?: Partial<Record<keyof FormState, string>>; error?: string };
        if (data.fields) setErrors(data.fields);
        else setFormError(data.error ?? "Something went wrong. Please try again.");
      } else {
        setSent(true);
      }
    } catch {
      setFormError("Network error. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const fieldClass = (k: keyof FormState) => `liquid-glass rounded-2xl px-4 py-3 ${errors[k] ? "ring-1 ring-red-400/60" : ""}`;
  const inputClass = "bg-transparent border-0 outline-none w-full text-white placeholder:text-white/30 text-sm";

  return (
    <section id="contact" className="bg-black py-28 md:py-40 px-6 overflow-hidden relative">
      <div className="absolute inset-0 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(ellipse at bottom, rgba(255,255,255,0.04) 0%, transparent 60%)" }} />
      <div className="max-w-6xl mx-auto relative">
        <Reveal><div className="text-white/40 text-sm tracking-widest uppercase">Contact</div></Reveal>
        <Reveal delay={100}>
          <h2 className="font-instrument text-5xl md:text-7xl lg:text-8xl text-white tracking-tight mt-6 leading-[1.05]">
            Tell us what you're <em className="italic-serif text-white/50">building</em>.
          </h2>
        </Reveal>
        <Reveal delay={220}>
          <p className="mt-8 text-white/60 text-base md:text-lg leading-relaxed max-w-2xl">
            A defence-readiness audit, a custom AI product, a transformation roadmap, a prototype that needs hardening for production, or ongoing support for a live system. Start with a 30-minute discovery. No slide deck, no salesperson. Just an engineer. We reply within one business day.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8 mt-16">
          <Reveal delay={150}>
            <div className="liquid-glass rounded-3xl p-6 md:p-8">
              {sent ? (
                <div className="py-10 text-center">
                  <div className="liquid-glass rounded-full w-16 h-16 mx-auto flex items-center justify-center text-white text-2xl">✓</div>
                  <h3 className="font-instrument text-3xl text-white mt-6">Thanks, we've got your message.</h3>
                  <p className="text-white/60 text-sm mt-3 max-w-md mx-auto">An engineer from our team will reply within one business day.</p>
                  <button onClick={() => { setSent(false); setForm(EMPTY); }}
                          className="liquid-glass rounded-full px-6 py-2.5 text-white text-sm font-medium mt-6 hover:bg-white/5 transition-colors">
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-white/40 text-[10px] tracking-widest uppercase block mb-2">Full name</label>
                      <div className={fieldClass("name")}>
                        <input type="text" placeholder="Jane Doe" value={form.name} onChange={upd("name")} className={inputClass} />
                      </div>
                      {errors.name && <div className="text-red-300/90 text-xs mt-1.5">{errors.name}</div>}
                    </div>
                    <div>
                      <label className="text-white/40 text-[10px] tracking-widest uppercase block mb-2">Work email</label>
                      <div className={fieldClass("email")}>
                        <input type="email" placeholder="jane@company.com" value={form.email} onChange={upd("email")} className={inputClass} />
                      </div>
                      {errors.email && <div className="text-red-300/90 text-xs mt-1.5">{errors.email}</div>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    <div>
                      <label className="text-white/40 text-[10px] tracking-widest uppercase block mb-2">Company</label>
                      <div className={fieldClass("company")}>
                        <input type="text" placeholder="Acme Mining" value={form.company} onChange={upd("company")} className={inputClass} />
                      </div>
                    </div>
                    <div>
                      <label className="text-white/40 text-[10px] tracking-widest uppercase block mb-2">I'm interested in</label>
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => setDropdownOpen(o => !o)}
                          className="liquid-glass rounded-2xl px-4 py-3 w-full flex items-center justify-between text-sm text-white"
                        >
                          <span>{form.interest}</span>
                          <ChevronDown
                            size={14}
                            className={`text-white/40 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
                          />
                        </button>
                        {dropdownOpen && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 liquid-glass rounded-2xl overflow-hidden"
                               style={{ background: "rgba(10,10,10,0.97)", backdropFilter: "blur(16px)" }}>
                            {OPTIONS.map(opt => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => { setForm(f => ({ ...f, interest: opt })); setDropdownOpen(false); }}
                                className={`w-full text-left px-4 py-3 text-sm transition-colors hover:bg-white/[0.05] ${form.interest === opt ? "text-white font-medium" : "text-white/70"}`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="text-white/40 text-[10px] tracking-widest uppercase block mb-2">What are you trying to build?</label>
                    <div className={fieldClass("message")}>
                      <textarea placeholder="Briefly describe the problem, sector and timeline..." value={form.message} onChange={upd("message")} className={`${inputClass} resize-y min-h-[120px]`} />
                    </div>
                    {errors.message && <div className="text-red-300/90 text-xs mt-1.5">{errors.message}</div>}
                  </div>

                  {import.meta.env.VITE_TURNSTILE_SITE_KEY && (
                    <div className="mt-4">
                      <div id="turnstile-widget" />
                    </div>
                  )}

                  {formError && (
                    <div className="text-red-300/90 text-xs mt-3">{formError}</div>
                  )}

                  <div className="flex items-center justify-between mt-6 gap-3">
                    <div className="text-white/40 text-xs tracking-widest uppercase">We reply within 1 business day</div>
                    <button type="submit" disabled={sending}
                            className="rounded-full px-7 py-3 text-black text-sm font-medium bg-white hover:bg-white/90 transition-colors inline-flex items-center gap-2 disabled:opacity-60">
                      {sending ? "Sending…" : "Send message"} {!sending && <ArrowRight size={16} />}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>

          <Reveal delay={250}>
            <div className="flex flex-col gap-3">
              {[
                { icon: <Mail size={18} />,   label: "Email",   val: "info@paisums.com" },
                { icon: <MapPin size={18} />, label: "Offices", val: "Perth · Auckland" },
                { icon: <Shield size={18} />, label: "Trust",   val: "NDA on request · Defence-ready by design" },
              ].map((row) => (
                <div key={row.label} className="liquid-glass rounded-2xl p-5 flex items-start gap-4">
                  <div className="liquid-glass rounded-xl p-2.5 text-white/80 flex-none">{row.icon}</div>
                  <div className="min-w-0">
                    <div className="text-white/40 text-[10px] tracking-widest uppercase">{row.label}</div>
                    <div className="font-instrument text-lg text-white mt-0.5 truncate">{row.val}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
